import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { catchError, EMPTY, filter, of, pipe, switchMap, tap } from 'rxjs';

import { getPlatformKeysWithSuggestions, isNotNil, type MalouErrorCode } from '@malou-io/package-utils';

import type { Platform } from '#modules/platforms/models/platform';
import type { Restaurant } from '#modules/restaurants/models/restaurant';

import { SuggestionsApiService } from '#modules/informations/services/suggestions.service';
import { type Comparison, DEFAULT_COMPARISON_KEYS, PlatformComparison } from '#modules/platforms/models/platform-comparison';
import { RestaurantStore } from '#modules/restaurants/stores/restaurant.store';

export type SuggestionMalouErrorCode = MalouErrorCode.GMB_PIN_DROP_REQUIRED | MalouErrorCode.GMB_STOREFRONT_REQUIRED_FOR_CATEGORY | null;

interface SuggestionsState {
    comparisons: Comparison[];
    malouErrorCode: SuggestionMalouErrorCode;
    cachedRestaurantId: string | null;
}

const initialState: SuggestionsState = {
    comparisons: [],
    malouErrorCode: null,
    cachedRestaurantId: null,
} as const;

const PLATFORM_KEYS_WITH_SUGGESTIONS = getPlatformKeysWithSuggestions();

export const SuggestionsStore = signalStore(
    withState(initialState),
    withComputed(({ comparisons }) => ({
        suggestionsCount: computed(() => comparisons().length),
    })),
    withMethods((store, suggestionsApiService = inject(SuggestionsApiService), translateService = inject(TranslateService)) => {
        const _canHaveSuggestions = (restaurant: Restaurant): boolean =>
            (restaurant.access ?? []).some((access) => PLATFORM_KEYS_WITH_SUGGESTIONS.includes(access.platformKey));

        const loadSuggestions = rxMethod<Restaurant | null>(
            pipe(
                tap((restaurant) => {
                    if (!restaurant || !_canHaveSuggestions(restaurant)) {
                        patchState(store, initialState);
                    }
                }),
                filter(isNotNil),
                filter((restaurant) => _canHaveSuggestions(restaurant) && restaurant.id !== store.cachedRestaurantId()),
                switchMap((restaurant) =>
                    suggestionsApiService.getSuggestions$(restaurant._id).pipe(
                        tap((result) => {
                            const restaurantPlatform = result?.data?.suggestionData as Platform; // @Baptiste "as Platform" is not clean but this needs big refacto in backend + frontend and we don't want to do it in this PR
                            const allComparisons = new PlatformComparison(
                                restaurant,
                                restaurantPlatform,
                                translateService
                            ).getDifferentComparisonsByKeys(DEFAULT_COMPARISON_KEYS);
                            const comparisons = allComparisons?.filter((data) => result?.data?.suggestionKeys?.includes(data.key)) ?? [];
                            patchState(store, {
                                comparisons,
                                malouErrorCode: result?.data?.malouErrorCode ?? null,
                            });
                        }),
                        catchError(() => {
                            patchState(store, initialState);
                            return EMPTY;
                        })
                    )
                )
            )
        );

        return {
            loadSuggestions,
            resetAndReload(restaurant: Restaurant): void {
                patchState(store, initialState);
                loadSuggestions(of(restaurant));
            },
        };
    })
);

export function injectSuggestionsStore(): InstanceType<typeof SuggestionsStore> {
    const store = inject(SuggestionsStore);
    const restaurantStore = inject(RestaurantStore);

    store.loadSuggestions(restaurantStore.restaurant);
    return store;
}
